import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { DeleteModal } from "@View/components/DeleteModal";
import { connect, useSelector } from "react-redux";
import DataTable from "@View/components/table/DataTable";
import { Tooltip } from "@material-ui/core";
import EditIcon from "@View/components/svgComponents/EditIcon";
import DeleteIcon from "@View/components/svgComponents/DeleteIcon";
import ButtonAddNew from "@View/components/buttons/ButtonAddNew";
import NewGroupModal from "@View/pages/AdminPO/customer/customerDetails/modals/NewGroupModal";
import EditGroupModal from "@View/pages/AdminPO/customer/customerDetails/modals/EditGroupModal";
import { useTooltipStyle } from "@View/style/components/tooltipStyles";
import {
  canAddAbility,
  canDeleteGroup,
  canEditEverything,
} from "@Security/stateHelpers";
import { getCustomerGroups } from "@State/actions/actionsGet/actionGetCustomer";
import getAPI from "@API/index";
import DetailsIcon from "@View/components/svgComponents/DetailsIcon";
import NewGroupConsolidationModal from "@View/pages/AdminPO/customer/customerDetails/modals/NewGroupConsolidationModal";
import NewRequestIcon from "@View/components/svgComponents/NewRequestIcon";
import { IsConsolidationEnabled } from "@Constants/tokens";

const mapStateToProps = (state) => {
  return {
    customer: state.customer.customer,
    groups: state.customer.groups,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    refreshGroups: (customerId) => dispatch(getCustomerGroups(customerId)),
  };
};

const GroupsTable = ({ customer, groups, refreshGroups }) => {
  const { id: customerId } = useParams();

  const [deletedItem, setDeletedItem] = useState();
  const [selectedItem, setItem] = useState();
  const clients = useSelector(
    (state) => state.customer?.customer?.clients ?? []
  );

  const refreshItems = async () => {
    refreshGroups(customer.id);
  };

  const deleteItem = async (item) => {
    await getAPI().request({
      method: "delete",
      url: `/api/Customer/${customerId}/groups/${item.id}`,
    });
    refreshItems();
    setDeletedItem();
  };

  useEffect(() => {
    refreshItems();
  }, [customerId]);

  const { t } = useTranslation();
  const tooltipStyle = useTooltipStyle();

  const confirmDeletionModal = (event, item) => {
    event.preventDefault();
    setDeletedItem(item);
  };

  const handleCloseModal = (event) => {
    event.preventDefault();
    setDeletedItem();
  };

  const handleDelete = (event) => {
    event.preventDefault();
    deleteItem(deletedItem);
  };

  const [openNew, setOpenNew] = React.useState(false);

  const handleOpenNew = () => {
    setOpenNew(true);
  };

  const handleCloseNew = () => {
    setOpenNew(false);
  };

  const [openEdit, setOpenEdit] = React.useState(false);
  const handleOpenEdit = () => {
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const [groupData, setGroupData] = React.useState(null);
  const [openNewConsolidation, setOpenNewConsolidation] = React.useState(false);
  const handleOpenNewConsolidation = () => {
    setOpenNewConsolidation(true);
  };
  const handleCloseNewConsolidation = () => {
    setOpenNewConsolidation(false);
  };

  const tableHeader = [
    {
      field: "name",
      headerName: t("inputs.Label"),
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "capacity",
      headerName: t("ClientCapacityTitle"),
      flex: 2,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        const clients = cell.row.clients || [];
        const visibleCount = 2;
        const visibleClients = clients.slice(0, visibleCount);
        const remainingCount = clients.length - visibleCount;

        const fullList = clients.map(c => `${c.name} (ИНН: ${c.externalId || "—"})`).join("\n");

        return (
          <Tooltip
            title={<div style={{ whiteSpace: "pre-line" }}>{fullList}</div>}
            classes={tooltipStyle}
            placement="bottom-start"
          >
            <div style={{ lineHeight: "18px", fontWeight: 400, cursor: "pointer" }}>
              {visibleClients.map((client) => (
                <div key={client.id}>
                  {client.name} (ИНН: {client.externalId || "—"})
                </div>
              ))}
              {remainingCount > 0 && (
                <div style={{ color: "#2960EE", fontWeight: 500 }}>
                  +{remainingCount} ещё...
                </div>
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: " ",
      flex: 1,
      headerAlign: "left",
      align: "left",
      renderCell: (cell) => {
        return (
          <div className="tableIcons">
            {IsConsolidationEnabled() && (
              <Tooltip title={t("h1.New Request")} classes={tooltipStyle}>
                <div
                  onClick={() => {
                    setGroupData(cell.row);
                    handleOpenNewConsolidation();
                  }}
                  className={tooltipStyle.element}
                >
                  <NewRequestIcon />
                </div>
              </Tooltip>
            )}
            {IsConsolidationEnabled() && (
              <Tooltip title={t("buttons.Details")} classes={tooltipStyle}>
                <Link
                  to={`/customer/details/${customerId}/group/${cell.row.id}`}
                  className={tooltipStyle.element}
                >
                  <DetailsIcon />
                </Link>
              </Tooltip>
            )}
            {canEditEverything() && canAddAbility() && (
              <>
                <Tooltip title={t("buttons.Edit")} classes={tooltipStyle}>
                  <div
                    onClick={() => {
                      setItem(cell.row);
                      handleOpenEdit();
                    }}
                    className={tooltipStyle.element}
                  >
                    <EditIcon />
                  </div>
                </Tooltip>
                {canDeleteGroup() && (
                  <Tooltip title={t("buttons.Delete")} classes={tooltipStyle}>
                    <div
                      onClick={(event) => confirmDeletionModal(event, cell.row)}
                      className={tooltipStyle.element}
                    >
                      <DeleteIcon />
                    </div>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <NewGroupModal
        customerId={customerId}
        open={openNew}
        handleOpen={handleOpenNew}
        handleClose={handleCloseNew}
        handleUpdate={refreshItems}
        groups={groups}
      />
      {selectedItem && (
        <EditGroupModal
          group={selectedItem}
          customerId={customerId}
          open={openEdit}
          handleOpen={handleOpenEdit}
          handleClose={handleCloseEdit}
          handleUpdate={refreshItems}
          groups={groups}
        />
      )}
      {groupData && (
        <NewGroupConsolidationModal
          open={openNewConsolidation}
          customerId={customerId}
          group={groupData}
          handleOpen={handleOpenNewConsolidation}
          handleClose={handleCloseNewConsolidation}
          reloadData={refreshItems}
        />
      )}
      <DataTable
        rows={groups}
        columns={tableHeader}
        toolbarButtons={canAddAbility() && <ButtonAddNew onClick={handleOpenNew} />}
        rowHeight={70}
      />
      <DeleteModal
        handleDelete={handleDelete}
        openModal={!!deletedItem}
        handleCloseModal={handleCloseModal}
      />
    </React.Fragment>
  );
};

GroupsTable.propTypes = {
  clients: PropTypes.array.isRequired,
  deleteClient: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupsTable);
